import { FinanceCurrency } from "@/features/finance/model/finance-types"
import { ChevronDownIcon } from "lucide-react"
import { CardContent, CardDescription, CardHeader, CardTitle, Collapsible, CollapsibleContent, CollapsibleTrigger, Label, SurfaceCard } from "@ibada/ui"
import { Select, type SelectOption } from "@ibada/ui"
import { useTranslations } from "@/i18n"
import { useIsMobile } from "@/shared/hooks/use-mobile"
import { MobileFormError, MobileFormSection, MobileFormSelectRow } from "@/shared/components/mobile-form"

const CURRENCIES = Object.values(FinanceCurrency)

type CurrencySettingsSectionProps = {
  selectKey?: string
  title: string
  description: string
  baseCurrencyLabel: string
  helperText: string
  baseCurrency: FinanceCurrency
  canEdit: boolean
  baseCurrencyError?: string
  onBaseCurrencyChange: (value: FinanceCurrency) => void
  defaultOpen?: boolean
}

export function CurrencySettingsSection({
  selectKey,
  title,
  description,
  baseCurrencyLabel,
  helperText,
  baseCurrency,
  canEdit,
  baseCurrencyError,
  onBaseCurrencyChange,
  defaultOpen = false,
}: CurrencySettingsSectionProps) {
  const currencyOptions: SelectOption<FinanceCurrency>[] = CURRENCIES.map((currency) => ({ value: currency, label: currency }))
  const isMobile = useIsMobile()
  const tMobile = useTranslations("common.mobileForm")

  if (isMobile) {
    // Phone: one titled group, always open; the currency opens a picker sheet (the change is confirmed after).
    return (
      <div>
        <MobileFormSection title={title} footer={helperText}>
          <MobileFormSelectRow
            label={baseCurrencyLabel}
            placeholder={tMobile("choose")}
            value={baseCurrency}
            onChange={(value) => value && onBaseCurrencyChange(value as FinanceCurrency)}
            options={CURRENCIES.map((currency) => ({ value: currency, label: currency }))}
            doneLabel={tMobile("done")}
            emptyLabel={tMobile("noResults")}
            disabled={!canEdit}
            invalid={Boolean(baseCurrencyError)}
          />
        </MobileFormSection>
        <MobileFormError message={baseCurrencyError} />
      </div>
    )
  }

  return (
    <Collapsible defaultOpen={defaultOpen} className='group/collapsible'>
      <SurfaceCard className='text-card-foreground group/card overflow-hidden py-2 text-sm'>
        <CardHeader className='pb-2'>
          <CollapsibleTrigger asChild>
            <button
              type='button'
              className='ring-sidebar-ring hover:bg-sidebar-accent/30 flex w-full items-start justify-between gap-3 rounded-md px-2 py-2 text-left outline-hidden transition-colors focus-visible:ring-2'
            >
              <div className='space-y-1'>
                <CardTitle>{title}</CardTitle>
                <CardDescription>{description}</CardDescription>
              </div>
              <ChevronDownIcon className='text-muted-foreground mt-1 size-4 shrink-0 transition-transform group-data-[state=open]/collapsible:rotate-180' />
            </button>
          </CollapsibleTrigger>
        </CardHeader>

        <CollapsibleContent>
          <CardContent className='w-full space-y-1.5'>
            <Label>{baseCurrencyLabel}</Label>
            <Select
              key={selectKey}
              value={baseCurrency}
              disabled={!canEdit}
              onValueChange={onBaseCurrencyChange}
              options={currencyOptions}
            />
            <p className='text-muted-foreground text-xs'>{helperText}</p>
            {baseCurrencyError ? <p className='text-destructive text-xs'>{baseCurrencyError}</p> : null}
          </CardContent>
        </CollapsibleContent>
      </SurfaceCard>
    </Collapsible>
  )
}
